/**
 * Author: Devon
 *
 * Context: FeatureFlagContextManager
 * Description:
 * - FeatureFlagContextManager is a context provider that manages the state of feature flags.
 * - It uses a query parameter to store the feature flag state and provides methods to update or toggle feature flags.
 * - The component synchronizes the feature flag state across the application and makes the flags accessible via context.
 *
 * Hooks: useFeatureFlag
 * Description:
 * - useFeatureFlag is a custom hook that provides the current value of a specific feature flag and methods to modify its state.
 */

/* eslint-disable no-bitwise -- We want feature flags to be opaque */

import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';

import useQueryParamState from 'watchtower-ui/customHooks/useQueryParamState';
import { isDerivedValue } from 'watchtower-ui/utils/utilityFunctions';

export const FEATURE_FLAG_QUERY_PARAM = 'expFeatFlags';

const FEATURE_FLAGS = {
  DEVELOPER_LOGS: 1, // 1 << 0
  HELP_PAGE: 2, // 1 << 1
  SHOW_ERROR_BOUNDARY_REPORT_BUTTON: 4, // 1 << 2
  SHOW_COLORS_PATTERNS: 8, // 1 << 3
  SHOW_UPPER_BOUND: 16, // 1 << 4
  SHOW_CUSTOM_MONTH: 32, // 1 << 5
  SHOW_POD_OPTIMIZER: 64, // 1 << 6
  SHOW_ALL_DATES: 128, // 1 << 7
  SHOW_PRECISE_NUMBERS: 256, // 1 << 8
  SHOW_VERTICAL_FILTER: 512, // 1 << 9
  SHOW_NEW_OPTIMIZATION: 1024, // 1 << 10
  SHOW_LEARNED_CURVES: 4096, // 1 << 12
  SHOW_CUT_BUDGET: 8192, // 1 << 13
  SHOW_ADD_BUDGET: 16384, // 1 << 14
} as const;

type InternalFeatureFlag = keyof typeof FEATURE_FLAGS;
type FeatureFlag = Exclude<InternalFeatureFlag, `_${string}`>;

export type FeatureFlagState = { [K in FeatureFlag]?: boolean };
export type FullFeatureFlagState = { [K in FeatureFlag]: boolean };

const isFeatureFlag = (key: InternalFeatureFlag): key is FeatureFlag => !key.startsWith('_');
const FEATURE_FLAG_KEYS = (Object.keys(FEATURE_FLAGS) as InternalFeatureFlag[]).filter(isFeatureFlag);
const FEATURE_FLAG_MASK = FEATURE_FLAG_KEYS.reduce((mask, key) => mask | FEATURE_FLAGS[key], 0);
const defaultFlagValue = (parseInt(import.meta.env.VITE_DEFAULT_FEATURE_FLAG as string, 2) || 0) & FEATURE_FLAG_MASK;

const toFeatureFlagState = (featureFlagInt: number): FeatureFlagState => {
  if (!Number.isInteger(featureFlagInt)) return {};

  const processedFlags = (featureFlagInt ^ defaultFlagValue) & FEATURE_FLAG_MASK;

  return Object.fromEntries(
    FEATURE_FLAG_KEYS.filter((key) => (processedFlags & FEATURE_FLAGS[key]) !== 0).map((key) => [key, true]),
  );
};

const toFeatureFlagInt = (featureFlagState: FeatureFlagState) =>
  FEATURE_FLAG_KEYS.reduce((flags, key) => (featureFlagState[key] ? flags | FEATURE_FLAGS[key] : flags), 0) ^
  defaultFlagValue;

type FeatureFlagContextValue = {
  featureFlags: FeatureFlagState;
  fullFeatureFlags: FullFeatureFlagState;
  setFeatureFlags?: Dispatch<SetStateAction<FeatureFlagState>>;
};

const FeatureFlagContext = createContext<FeatureFlagContextValue>({
  featureFlags: {},
  fullFeatureFlags: Object.fromEntries(FEATURE_FLAG_KEYS.map((k) => [k, false])) as FullFeatureFlagState,
});
const NO_FEATURE_FLAG_OVERRIDES: FeatureFlagState = {};

interface FeatureFlagContextManagerProps {
  children: ReactNode;
  featureFlags?: FeatureFlagState;
}

export const FeatureFlagContextManager = ({
  children,
  featureFlags: featureFlagOverrides = NO_FEATURE_FLAG_OVERRIDES,
}: Readonly<FeatureFlagContextManagerProps>) => {
  const [, featureFlagInt, setFeatureFlagInt] = useQueryParamState(FEATURE_FLAG_QUERY_PARAM, 0);
  const featureFlagState = useMemo(() => {
    const urlState = toFeatureFlagState(featureFlagInt);
    // Merge with prop-based feature flags (override flags take precedence, primarily for tests)
    return { ...urlState, ...featureFlagOverrides };
  }, [featureFlagInt, featureFlagOverrides]);
  const fullFeatureFlagState = useMemo(
    () => Object.fromEntries(FEATURE_FLAG_KEYS.map((k) => [k, Boolean(featureFlagState[k])])) as FullFeatureFlagState,
    [featureFlagState],
  );
  useEffect(() => {
    if (import.meta.env.DEV || fullFeatureFlagState.DEVELOPER_LOGS) {
      console.debug('Feature flag state', fullFeatureFlagState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We explicitly only want to refresh when the source state changes
  }, [featureFlagInt]);
  const setFeatureFlagState = useCallback<Dispatch<SetStateAction<FeatureFlagState>>>(
    (newVal) => {
      if (isDerivedValue(newVal)) {
        setFeatureFlagInt((cur) => toFeatureFlagInt(newVal(toFeatureFlagState(cur ?? 0))));
      } else {
        setFeatureFlagInt(toFeatureFlagInt(newVal));
      }
    },
    [setFeatureFlagInt],
  );
  const contextValue = useMemo<FeatureFlagContextValue>(
    () => ({
      featureFlags: featureFlagState,
      fullFeatureFlags: fullFeatureFlagState,
      setFeatureFlags: setFeatureFlagState,
    }),
    [featureFlagState, fullFeatureFlagState, setFeatureFlagState],
  );
  return <FeatureFlagContext.Provider value={contextValue}>{children}</FeatureFlagContext.Provider>;
};

export const useFeatureFlag = (
  featureFlag: FeatureFlag,
): [boolean, { set: Dispatch<SetStateAction<boolean>>; toggle: () => void }] => {
  const featureFlagValue = useContextSelector(FeatureFlagContext, (context) => context.fullFeatureFlags[featureFlag]);
  const setFeatureFlags = useContextSelector(FeatureFlagContext, (context) => context.setFeatureFlags);
  const setFeatureFlagValue = useCallback<Dispatch<SetStateAction<boolean>>>(
    (newVal) => {
      if (setFeatureFlags) {
        if (isDerivedValue(newVal)) {
          setFeatureFlags((cur) => ({ ...cur, [featureFlag]: newVal(Boolean(cur[featureFlag])) }));
        } else {
          setFeatureFlags((cur) => ({ ...cur, [featureFlag]: newVal }));
        }
      }
    },
    [featureFlag, setFeatureFlags],
  );
  const toggleFeatureFlagValue = useCallback(() => {
    if (setFeatureFlags) {
      setFeatureFlags((cur) => ({ ...cur, [featureFlag]: !cur[featureFlag] }));
    }
  }, [featureFlag, setFeatureFlags]);
  return [featureFlagValue, { set: setFeatureFlagValue, toggle: toggleFeatureFlagValue }];
};
